/**
 * Botão flutuante do WebChat - Versão Premium
 * Com animações, pulso de novas mensagens, dark mode e drag touch mobile
 *
 * @version 2.1.0 - Adicionado modo "minimize" (mini-pill discreta na borda)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createLogger } from '@/lib/logger';

const logger = createLogger('FloatingButton');

interface FloatingButtonProps {
  onClick: () => void;
  primaryColor: string;
  position: 'bottom-right' | 'bottom-left';
  unreadCount: number;
  isOpen: boolean;
  isDarkMode?: boolean;
  // Props para modo border-logo
  buttonStyle?: 'solid' | 'border-logo';
  buttonBorderWidth?: number;
  companyLogoUrl?: string | null;
  // Oculta botão quando chat aberto em mobile (evita obstruir input)
  hideOnMobileOpen?: boolean;
  // NOVO v2.1.0: Permite ao visitante minimizar o widget em uma mini-pill
  allowMinimize?: boolean;
  // Usado como chave de persistência do estado minimizado por inbox
  inboxId?: string;
}

export const FloatingButton: React.FC<FloatingButtonProps> = ({
  onClick,
  primaryColor,
  position,
  unreadCount,
  isOpen,
  isDarkMode = false,
  buttonStyle = 'solid',
  buttonBorderWidth = 4,
  companyLogoUrl,
  hideOnMobileOpen = false,
  allowMinimize = false,
  inboxId,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showPulse, setShowPulse] = useState(false);

  // Estado do modo minimizado, persistido em localStorage por inbox
  const storageKey = inboxId ? `uaizychat:minimized:${inboxId}` : null;
  const [isMinimized, setIsMinimized] = useState<boolean>(() => {
    if (!storageKey || typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  // Persiste alteração do estado minimizado
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      if (isMinimized) {
        window.localStorage.setItem(storageKey, '1');
      } else {
        window.localStorage.removeItem(storageKey);
      }
    } catch (err) {
      logger.warn('Falha ao persistir estado minimizado', err as any);
    }
  }, [isMinimized, storageKey]);

  // Se a config for desligada em runtime, força restaurar
  useEffect(() => {
    if (!allowMinimize && isMinimized) {
      setIsMinimized(false);
    }
  }, [allowMinimize, isMinimized]);

  // Estados para drag touch (mobile)
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const dragStartPos = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);
  const wasDragged = useRef(false);

  // Ativa pulso quando há novas mensagens
  useEffect(() => {
    if (unreadCount > 0 && !isOpen) {
      setShowPulse(true);
    } else {
      setShowPulse(false);
    }
  }, [unreadCount, isOpen]);

  // Ao chegar nova mensagem enquanto minimizado, restaura automaticamente
  useEffect(() => {
    if (isMinimized && unreadCount > 0) {
      setIsMinimized(false);
    }
  }, [unreadCount, isMinimized]);

  // ===== HANDLERS DE DRAG TOUCH (MOBILE) =====
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    dragStartPos.current = {
      x: touch.clientX,
      y: touch.clientY,
      startX: rect.left,
      startY: rect.top,
    };
    wasDragged.current = false;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!dragStartPos.current) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStartPos.current.x;
    const deltaY = touch.clientY - dragStartPos.current.y;
    if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
      setIsDragging(true);
      wasDragged.current = true;
      e.preventDefault();
      const newX = Math.max(10, Math.min(window.innerWidth - 70, dragStartPos.current.startX + deltaX));
      const newY = Math.max(10, Math.min(window.innerHeight - 70, dragStartPos.current.startY + deltaY));
      setDragPosition({ x: newX, y: newY });
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    dragStartPos.current = null;
  }, []);

  // Click principal: abre chat (ou expande da mini-pill)
  const handleClick = useCallback(() => {
    if (wasDragged.current) {
      wasDragged.current = false;
      return;
    }
    if (isMinimized) {
      setIsMinimized(false);
      return;
    }
    onClick();
  }, [onClick, isMinimized]);

  // Handler para o "X" que minimiza (stopPropagation evita abrir o chat)
  const handleMinimize = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsMinimized(true);
  }, []);

  // Oculta botão quando chat aberto em mobile
  if (hideOnMobileOpen && isOpen) {
    return null;
  }

  // Container base
  const containerStyles: React.CSSProperties = dragPosition
    ? {
        position: 'fixed',
        left: `${dragPosition.x}px`,
        top: `${dragPosition.y}px`,
        right: 'auto',
        bottom: 'auto',
        zIndex: 9999,
        transition: isDragging ? 'none' : 'all 0.3s ease',
      }
    : {
        position: 'fixed',
        bottom: '20px',
        [position === 'bottom-right' ? 'right' : 'left']: '20px',
        zIndex: 9999,
      };

  // ===== MINI-PILL (estado minimizado) =====
  // Pequeno círculo de 28px que fica discreto e permite restaurar o widget
  if (isMinimized && !isOpen) {
    const pillContainerStyles: React.CSSProperties = {
      position: 'fixed',
      bottom: '12px',
      [position === 'bottom-right' ? 'right' : 'left']: '12px',
      zIndex: 9999,
    };
    const pillStyles: React.CSSProperties = {
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      backgroundColor: buttonStyle === 'border-logo' ? '#ffffff' : primaryColor,
      border: buttonStyle === 'border-logo' ? `2px solid ${primaryColor}` : 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: `0 2px 8px rgba(0,0,0,0.15)`,
      opacity: isHovered ? 1 : 0.75,
      transition: 'all 0.2s ease',
      transform: isHovered ? 'scale(1.1)' : 'scale(1)',
      padding: 0,
      overflow: 'hidden',
    };
    return (
      <div style={pillContainerStyles}>
        <button
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={pillStyles}
          aria-label="Restaurar chat"
          title="Abrir chat"
        >
          {companyLogoUrl ? (
            <img
              src={companyLogoUrl}
              alt=""
              style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={buttonStyle === 'border-logo' ? primaryColor : '#ffffff'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          )}
        </button>
        {/* Badge de não lidos permanece visível para não perder mensagens */}
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              backgroundColor: '#ef4444',
              color: '#ffffff',
              borderRadius: '10px',
              minWidth: '18px',
              height: '18px',
              padding: '0 5px',
              fontSize: '10px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #ffffff',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>
    );
  }

  // Estilos do botão principal
  const buttonStyles: React.CSSProperties =
    buttonStyle === 'border-logo'
      ? {
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: '#ffffff',
          border: `${buttonBorderWidth}px solid ${primaryColor}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isHovered
            ? `0 8px 25px ${primaryColor}40, 0 4px 12px rgba(0,0,0,0.15)`
            : `0 4px 15px ${primaryColor}30, 0 2px 8px rgba(0,0,0,0.1)`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isHovered ? 'scale(1.08)' : 'scale(1)',
          position: 'relative',
          overflow: 'hidden',
        }
      : {
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: primaryColor,
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isHovered
            ? `0 8px 25px ${primaryColor}40, 0 4px 12px rgba(0,0,0,0.15)`
            : `0 4px 15px ${primaryColor}30, 0 2px 8px rgba(0,0,0,0.1)`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isHovered ? 'scale(1.08)' : 'scale(1)',
          position: 'relative',
        };

  const pulseStyles: React.CSSProperties = {
    position: 'absolute',
    inset: '-4px',
    borderRadius: '50%',
    backgroundColor: primaryColor,
    opacity: 0.4,
    animation: 'buttonPulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  };

  const badgeStyles: React.CSSProperties = {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    borderRadius: '12px',
    minWidth: '22px',
    height: '22px',
    padding: '0 6px',
    fontSize: '12px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)',
    animation: unreadCount > 0 ? 'badgeBounce 0.3s ease-out' : 'none',
    border: '2px solid #ffffff',
  };

  const iconContainerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
  };

  const ChatIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={buttonStyle === 'border-logo' ? primaryColor : '#ffffff'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );

  const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={buttonStyle === 'border-logo' ? primaryColor : '#ffffff'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  const logoStyles: React.CSSProperties = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    objectFit: 'cover',
  };

  const renderButtonContent = () => {
    if (isOpen) return <CloseIcon />;
    if (buttonStyle === 'border-logo' && companyLogoUrl) {
      return (
        <img
          src={companyLogoUrl}
          alt="Logo"
          style={logoStyles}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      );
    }
    return <ChatIcon />;
  };

  // Botão "X" para minimizar (aparece no hover quando allowMinimize=true e chat fechado)
  const showMinimizeBtn = allowMinimize && !isOpen && isHovered;
  const minimizeBtnStyles: React.CSSProperties = {
    position: 'absolute',
    top: '-6px',
    right: position === 'bottom-right' ? '-6px' : 'auto',
    left: position === 'bottom-left' ? '-6px' : 'auto',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    color: '#374151',
    border: '1px solid rgba(0,0,0,0.1)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    zIndex: 2,
    animation: 'fadeInScale 0.15s ease-out',
  };

  return (
    <>
      <style>
        {`
          @keyframes buttonPulse {
            0% { transform: scale(1); opacity: 0.4; }
            70% { transform: scale(1.5); opacity: 0; }
            100% { transform: scale(1.5); opacity: 0; }
          }
          @keyframes borderBreathing {
            0%, 100% { transform: scale(1); opacity: 0.35; }
            50% { transform: scale(1.06); opacity: 0.15; }
          }
          @keyframes badgeBounce {
            0% { transform: scale(0); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }
          @keyframes buttonEnter {
            0% { opacity: 0; transform: scale(0) rotate(-180deg); }
            100% { opacity: 1; transform: scale(1) rotate(0deg); }
          }
          @keyframes fadeInScale {
            0% { opacity: 0; transform: scale(0.6); }
            100% { opacity: 1; transform: scale(1); }
          }
          .uaizychat-fab {
            animation: buttonEnter 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .uaizychat-fab:focus {
            outline: none;
            box-shadow: 0 0 0 3px ${primaryColor}40, 0 4px 15px ${primaryColor}30 !important;
          }
          .uaizychat-fab:active {
            transform: scale(0.95) !important;
          }
        `}
      </style>

      <div
        style={containerStyles}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Anel de respiração para modo border-logo */}
        {buttonStyle === 'border-logo' && !isOpen && (
          <div
            style={{
              position: 'absolute',
              inset: '-3px',
              borderRadius: '50%',
              border: `2px solid ${primaryColor}`,
              backgroundColor: 'transparent',
              opacity: 0.35,
              animation: 'borderBreathing 2.5s ease-in-out infinite',
              pointerEvents: 'none' as const,
            }}
          />
        )}

        {showPulse && <div style={pulseStyles} />}

        <button
          onClick={handleClick}
          style={buttonStyles}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="uaizychat-fab"
          aria-label={isOpen ? 'Fechar chat' : `Abrir chat${unreadCount > 0 ? `, ${unreadCount} mensagens não lidas` : ''}`}
          role="button"
          tabIndex={0}
        >
          <div style={iconContainerStyles}>{renderButtonContent()}</div>
        </button>

        {/* Mini-botão "X" para minimizar */}
        {showMinimizeBtn && (
          <button
            onClick={handleMinimize}
            style={minimizeBtnStyles}
            aria-label="Minimizar chat"
            title="Minimizar"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}

        {!isOpen && unreadCount > 0 && (
          <span style={badgeStyles} role="status" aria-live="polite">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>
    </>
  );
};
